import { useEffect, useLayoutEffect, useRef, useState } from 'react'

// M2.5: the tooltip is centred on its trigger, so on a narrow screen a trigger near
// either edge pushed it off-screen. Keep this in step with the page's px-6 gutter.
const TOOLTIP_MARGIN = 24

// Figma 29.09 `pricing` (spec home-page-arvance-spec.29.09.md R18-R24). `h` is the drawn row height
// from canvas up (§6 D18: 48 and 56 are mixed in the frame); md..canvas is undrawn, so there it is a
// min-height and a long value may wrap. Below md every row keeps min-h 58.
// The Users tooltip is drawn beside Ultimate's "4 Included / Add More", not beside the label, so it
// sits on that value (`valueTooltip`). Rent Comps and the Deal Presentations section are removed
// (canvas: "I will no longer add products that are Coming Soon status at this time").
const ROW_HEIGHT = {
  48: 'md:min-h-[48px] canvas:h-[48px]',
  56: 'md:min-h-[56px] canvas:h-[56px]',
}

const FEATURE_SECTIONS = [
  {
    title: 'Highlights',
    items: [
      {
        label: 'Properties Per Month',
        h: 48,
        tooltip:
          'This limit refers to the number of unique properties you can add to your pipeline each month. Once added, you can analyze each property as many times as you want.',
        starter: '20', pro: '250', ultimate: 'Unlimited',
      },
      {
        label: 'ARV Comps',
        h: 56,
        tooltip:
          'Starter includes the top 10 highest-ranked comparable sales. Pro and Ultimate unlock all available comparable sales for deeper market analysis.',
        starter: 'Top 10', pro: 'All Available', ultimate: 'All Available',
      },
      {
        label: 'Custom Templates',
        h: 56,
        tooltip:
          'Save custom deal analysis templates to quickly reuse financing assumptions, costs, and projections across future deals.',
        starter: '2', pro: 'Unlimited', ultimate: 'Unlimited',
      },
      {
        label: 'Users',
        h: 48,
        valueTooltip:
          'Need more seats? Additional users are $299/year each, billed annually and prorated. Add or remove users anytime in Account Settings.',
        starter: '1', pro: '1', ultimate: '4 Included / Add More',
      },
    ],
  },
  {
    title: 'Deal Analysis',
    items: [
      {
        label: 'Flip Analysis',
        h: 56,
        tooltip: (
          <>
            <p className="font-semibold mb-1">Includes:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Deal scoring</li>
              <li>Scenario modeling</li>
              <li>Financing structures</li>
              <li>Sales proceeds</li>
              <li>Custom templates</li>
            </ul>
          </>
        ),
        starter: true, pro: true, ultimate: true,
      },
      {
        label: 'ARV Analysis',
        h: 56,
        tooltip: (
          <>
            <p className="font-semibold mb-1">Includes:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Verified comps</li>
              <li>Advanced comp scoring</li>
              <li>DOM trends</li>
              <li>Automatic value adjustments</li>
              <li>Listing history &amp; photos</li>
            </ul>
          </>
        ),
        starter: true, pro: true, ultimate: true,
      },
      { label: 'Comp Scoring', h: 56, starter: true, pro: true, ultimate: true },
      { label: 'Deal Scoring', h: 56, starter: true, pro: true, ultimate: true },
    ],
  },
  {
    title: 'Deal Pipeline',
    items: [
      { label: 'Deal Tracking', h: 48, starter: true, pro: true, ultimate: true },
      { label: 'Property Dashboard', h: 48, starter: true, pro: true, ultimate: true },
      { label: 'Property Data & History', h: 48, starter: true, pro: true, ultimate: true },
      { label: 'Custom Tags', h: 48, starter: true, pro: true, ultimate: true },
      { label: 'Notes', h: 48, starter: true, pro: true, ultimate: true },
    ],
  },
]

const HelpTooltip = ({ content }) => {
  const [open, setOpen] = useState(false)
  // Horizontal correction applied on top of the -50% centring, in px.
  const [shift, setShift] = useState(0)
  const wrapRef = useRef(null)
  const tipRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDocPointer = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('pointerdown', onDocPointer)
    return () => document.removeEventListener('pointerdown', onDocPointer)
  }, [open])

  // Set #4, Desktop 4: appear on cursor hover, no click required. Gated to devices that
  // actually have a pointer for the same reason as the M2.6 colour fix above — a touch tap
  // fires a synthetic mouseenter with no matching mouseleave, so wiring hover unconditionally
  // would leave the tooltip stuck open after the finger lifts. On a hover-capable device a
  // real pointer click force-opens rather than toggles, so clicking the trigger while it's
  // already open on hover can't fight the hover state and instantly re-close it.
  const hoverCapable = () =>
    typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches
  const handleMouseEnter = () => { if (hoverCapable()) setOpen(true) }
  const handleMouseLeave = () => { if (hoverCapable()) setOpen(false) }

  // M2.5: measure once the tooltip is on screen and pull it back inside the gutter.
  // Measuring happens with shift already reset to 0, so the correction is absolute
  // rather than accumulating each time the tooltip is reopened.
  useLayoutEffect(() => {
    if (!open) {
      setShift(0)
      return
    }
    const el = tipRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const overflowLeft = TOOLTIP_MARGIN - rect.left
    const overflowRight = rect.right - (window.innerWidth - TOOLTIP_MARGIN)
    if (overflowLeft > 0) setShift(overflowLeft)
    else if (overflowRight > 0) setShift(-overflowRight)
  }, [open])

  return (
    <span
      ref={wrapRef}
      className="relative inline-flex"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        type="button"
        aria-label="More info"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          // Enter/Space activation dispatches a click with detail 0 (no real pointer
          // click happened), which is how this tells a keyboard toggle apart from a
          // mouse click without a separate onKeyDown — a keyboard user needs Enter to
          // close what Enter opened, since nothing else can reach a force-opened panel.
          const detail = e.detail ?? e.nativeEvent?.detail
          if (detail === 0) { setOpen((v) => !v); return }
          if (hoverCapable()) { setOpen(true); return }
          setOpen((v) => !v)
        }}
        /* M2.6: the active colour used to come from :hover, which sticks on a touch
           screen — so closing the tooltip left the trigger green until you tapped
           somewhere else. The colour now follows `open`, and :hover is limited to
           devices that actually have a pointer. */
        /* R22: the drawn `icon-tooltip` glyph at 16 x 16 (canvas: "Update all tooltip icons to 16x16
           px"), #C6CFC6 closed. Hover and open are not drawn and keep the live brand-primary. */
        className={`w-[16px] h-[16px] flex items-center justify-center transition-colors [@media(hover:hover)]:hover:text-brand-primary ${
          open ? 'text-brand-primary' : 'text-brand-fieldBdr'
        }`}
      >
        <svg width="16" height="16" viewBox="0 0 17.5 17.5" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M0 8.75C0 3.92 3.92 0 8.75 0S17.5 3.92 17.5 8.75 13.58 17.5 8.75 17.5 0 13.58 0 8.75ZM8.75 1.5a7.25 7.25 0 1 0 0 14.5 7.25 7.25 0 0 0 0-14.5Z" />
          <path d="M8.75 5.5c-.69 0-1.25.56-1.25 1.25v.11a.75.75 0 0 1-1.5 0v-.11A2.75 2.75 0 0 1 8.75 4h.1a2.6 2.6 0 0 1 1.7 4.63l-.67.66a.75.75 0 0 0-.38.67v.54a.75.75 0 0 1-1.5 0v-.54c0-.7.37-1.36.92-1.82l.79-.66a1.1 1.1 0 0 0-.86-1.98h-.1ZM8.75 13.75a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" />
        </svg>
      </button>
      <span
        ref={tipRef}
        role="tooltip"
        style={{ transform: `translateX(calc(-50% + ${shift}px))` }}
        className={`absolute left-1/2 bottom-full mb-2 w-[260px] rounded-lg bg-brand-dark text-white text-[12px] leading-[18px] p-3 shadow-lg transition-opacity z-30 text-left ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        {content}
      </span>
    </span>
  )
}

const renderCell = (value, emphasised, tooltip) => {
  if (value === true) {
    return (
      <div className="flex items-center justify-center">
        {/* R21: circle 24 #CBF7B7, dot 14 #4CBC18, white tick (`check_44`). */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <rect width="24" height="24" rx="12" fill="#CBF7B7" />
          <rect x="5" y="5" width="14" height="14" rx="7" fill="#4CBC18" />
          <path d="M9.1 12L11.15 14.25L14.9 10.25" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    )
  }
  if (value === false) {
    return <span className="text-[18px] font-medium text-brand-badgeMutedDark">—</span>
  }
  // D2.5.2: `md:font-bold` rather than plain `font-bold` — hover has no meaning below md,
  // and on a touch screen mouseenter fires on tap and never clears, which is exactly how
  // the tooltip trigger got stuck green in M2.6.
  const text = (
    <span className={`text-[16px] text-brand-dark transition-[font-weight] ${emphasised ? 'font-medium md:font-bold' : 'font-medium'}`}>
      {value}
    </span>
  )
  if (!tooltip) return text
  return (
    <span className="inline-flex items-center gap-2 md:gap-[14px]">
      {text}
      <HelpTooltip content={tooltip} />
    </span>
  )
}

// M2.2.1: the Starter/Pro/Ultimate tab strip that used to sit here is gone. Below md the
// three price columns are still too cramped to show at once, so the table still shows one
// plan at a time — but which one is now decided by the card you have swiped to in
// PlanCarousel, which is why `selectedPlan` arrives as a prop instead of living here.
const PlanFeatures = ({ selectedPlan, hoveredPlan }) => {
  // Selected column shows on mobile; all three show from md up.
  const colClass = (id) =>
    `${selectedPlan === id ? 'flex' : 'hidden'} md:flex justify-center`

  // D2.5.2 covers the Highlights block only — that is the section he named, and it is the
  // one with values worth comparing rather than rows of identical checkmarks.
  const emphasised = (sectionTitle, id) =>
    sectionTitle === 'Highlights' && hoveredPlan === id

  return (
    // D2.5: the table is 1040px wide — three 242px plan columns and a 242px label column
    // with 24px gaps (242×4 + 24×3 = 1040). The page gutter is 120px each side, leaving
    // only 960px, so the table steps 40px outside it on each side to reach its width.
    // Below canvas (no frame) the same -40 overhang holds and the columns flex instead.
    //
    // Review #3, M1.2: 24px from the cards to Highlights on mobile. The carousel track keeps an
    // 8px bottom pad for the hidden scrollbar, so 16 here is what measures 24 from the card edge.
    // Desktop is untouched — he filed this under Mobile.
    //
    // Figma 29.09: the Highlights head row starts at the cards' bottom edge and is 62 tall with the
    // title baseline 38 from its top (R19), so md+ has no margin above it; sections are 24 apart.
    <section className="w-full mt-[16px] md:mt-0 md:w-[calc(100%+80px)] md:-mx-[40px]">
      {/* last:mb-0 — the gap to the FAQs is owned by PricingFaq's mt, not stacked on top of it */}
      {FEATURE_SECTIONS.map((section) => (
        <div key={section.title} className="mb-[44px] md:mb-[24px] last:mb-0">
          <div className="pb-4 md:pb-0 md:pt-[17px] md:h-[62px] border-b border-brand-fieldBdr">
            {/* Review #3, M1.3: extra-bold, both widths. 800 comes from his word "extra-bold" —
                the Figma read endpoints were rate-limited when this was built, and README's rule is
                that a value he states in words outranks a frame. Flagged in the reply as his to
                correct if the file says 700. */}
            <h2 className="text-[20px] leading-[28px] text-left font-extrabold text-brand-dark">
              {section.title}
            </h2>
          </div>

          <div>
            {section.items.map((item) => (
              <div
                key={item.label}
                className={`grid grid-cols-[2fr_1fr] md:grid-cols-[minmax(0,1.3fr)_repeat(3,minmax(0,1fr))] md:gap-x-4 canvas:grid-cols-[1fr_242px_242px_242px] canvas:gap-x-[24px] items-center min-h-[58px] md:py-2 canvas:py-0 ${ROW_HEIGHT[item.h]} border-b border-brand-fieldBdr`}
              >
                {/* R22: the drawn icon centre is 14 + 8 after the text (the 17.5 glyph shown at 16). */}
                <div className="flex items-center gap-2 md:gap-[14px] flex-wrap text-left pr-2">
                  <span className="text-[14px] md:text-[16px] font-medium text-brand-dark text-left">
                    {item.label}
                  </span>
                  {item.tooltip && <HelpTooltip content={item.tooltip} />}
                </div>

                <div data-col="starter" className={colClass('starter')}>{renderCell(item.starter, emphasised(section.title, 'starter'))}</div>
                <div data-col="pro" className={colClass('pro')}>{renderCell(item.pro, emphasised(section.title, 'pro'))}</div>
                <div data-col="ultimate" className={colClass('ultimate')}>{renderCell(item.ultimate, emphasised(section.title, 'ultimate'), item.valueTooltip)}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </section>
  )
}

export default PlanFeatures
